import { useEffect, useState, useMemo, useCallback } from "react";
import { Route, Routes } from "react-router";
import PageNotFound from "./Components/PageNotFound/PageNotFound";
import Talk from "talkjs";
import { Session as TalkJSSession } from "@talkjs/react";
import DashboardLayout from "./Layout/DashboardLayout/DashboardLayout";
import Dashboard from "./Pages/Dashboard/Dashboard";
import Profile from "./Pages/Profile/Profile";
import Messages from "./Pages/Messages/Messages";
import OrganizationDetailsPage from "./Pages/Organizations/OrganizationDetailsPage";
import MyOrganizations from "./Pages/Organizations/MyOrganizations";
import SignUpOnboarding from "./Pages/SignUp/SignUpOnboarding/SignUpOnboarding";
import { useGetProfileQuery } from "../redux/reducers/apiSlice";
import OrganizationProfile from "./Pages/Organizations/OrganizationProfile/OrganizationProfile";
import { AddNewOrganization } from "./Pages/Organizations/AddNewOrganization/AddNewOrganization";
import AddNewOrganizationForm from "./Pages/Organizations/AddNewOrganizationForm/AddNewOrganizationForm";
import { EditOrganizationForm } from "./Pages/Organizations/EditOrganizationForm";
import FullPageNexusLogoAnimation from "./Reusable/FullPageNexusLogoAnimation/FullPageNexusLogoAnimation";
import GoalsManagement from "./Pages/Goals/GoalsManagement";
import PostDetails from "./Pages/Post/PostDetails";
import ViewAllSuggestions from "./Pages/ViewAllSuggestions/ViewAllSuggestions";
import useSignout from "./hooks/useSignout";
import toast from "react-hot-toast";
import SparksMap from "./Pages/SparksMap";
import Resources from "./Pages/Resources";
import CreateReport from "./Pages/Reports/CreateReport";
import ViewReport from "./Pages/Reports/ViewReport";
import GenerateAIReport from "./Pages/Reports/GenerateAIReport";

// @ts-ignore
const TALKJS_APP_ID = import.meta.env.VITE_TALKJS_APP_ID;

const AppRoutes = () => {
  const [isSocialLogin, setIsSocialLogin] = useState<boolean>(false);
  const [amountUnreads, setAmountUnreads] = useState(0);
  // Disable automatic refetching to prevent unnecessary re-renders
  const { data: profileData, isLoading: profileDataIsLoading, error: profileDataError } = useGetProfileQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  const { signOut } = useSignout();

  useEffect(() => {
    if (profileDataError) {
      const status = (profileDataError as { status?: string | number })?.status;
      if (status === "PARSING_ERROR") {
        toast.error(
          "Could not reach the API (invalid response). Check VITE_COMMUNITY_ENDPOINT in .env.local and restart the dev server."
        );
      } else {
        toast.error("Could not load your profile. Please sign in again.");
      }
      signOut();
    }
  }, [profileDataError, signOut]);

  // Memoize the onUnreadsChange callback to prevent TalkJS from remounting
  const handleUnreadsChange = useCallback((unreads: any[]) => {
    const unreadMessageCount = unreads.reduce(
      // @ts-ignore
      (acc, current) => acc + current.unreadMessageCount,
      0
    );
    setAmountUnreads(unreadMessageCount);
  }, []);

  // Extract stable primitive values from profileData to prevent unnecessary recalculations
  const userId = profileData?.id;
  const userName = profileData?.name ? `${profileData.name.firstName} ${profileData.name.lastName}` : '';
  const userPhotoUrl = profileData?.image?.url || undefined; // Normalize undefined/null to undefined
  const userEmail = profileData?.email || '';
  
  // Memoize the syncUser function - only recreate when user ID changes
  // This is the most critical dependency - if user ID doesn't change, don't recreate
  const syncUser = useMemo(() => {
    if (!userId) return undefined;
    
    return () =>
      new Talk.User({
        id: userId,
        name: userName,
        photoUrl: userPhotoUrl,
        email: userEmail,
        role: "default",
      });
  }, [userId, userName, userPhotoUrl, userEmail]);

  if (profileDataIsLoading) {
    return <FullPageNexusLogoAnimation />;
  }

  // Local development escape hatch: allow skipping onboarding when backend isn't seeded.
  // @ts-ignore
  const skipOnboarding = String(import.meta.env.VITE_SKIP_ONBOARDING || "").toLowerCase() === "true";
  const hasFinishedOnboarding = skipOnboarding || !!profileData?.onboarded;

  if (hasFinishedOnboarding && syncUser && userId) {
    return (
      <TalkJSSession
        key={userId} // Force remount only when user ID changes
        onUnreadsChange={handleUnreadsChange}
        appId={TALKJS_APP_ID}
        syncUser={syncUser}
      >
        <Routes>
          <Route
            path=""
            element={
              <DashboardLayout
                amountUnreads={amountUnreads}
              />
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route path="/users/:id" element={<Profile />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/sparks-map" element={<SparksMap />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/messages/:userId" element={<Messages />} />
            <Route path="/my-organizations" element={<MyOrganizations />} />
             <Route path="/resources" element={<Resources />} />
            <Route
              path="/organizations/:organizationId/sparks/:sparkName/goals"
              element={<GoalsManagement />}
            />
            <Route
              path="/organizations/:organizationId"
              element={<OrganizationDetailsPage />}
            />
            <Route path="/posts/:postId" element={<PostDetails />} />
            <Route
              path="/posts/:postId/interactions/:interactionId"
              element={<PostDetails />}
            />
            <Route path="/organizations/new" element={<AddNewOrganization />} />
            <Route
              path="/organizations/:organizationId/profile"
              element={<OrganizationProfile />}
            />
            <Route path="/reports/create" element={<CreateReport />} />
            <Route path="/reports/generate-ai" element={<GenerateAIReport />} />
            <Route path="/reports/:id" element={<ViewReport />} />
          </Route>
          <Route
            path="/organizations/new/form"
            element={<AddNewOrganizationForm />}
          />
          <Route
            path="/organizations/:organizationId/edit"
            element={<EditOrganizationForm />}
          />
          <Route path="/suggestions" element={<ViewAllSuggestions />} />

          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </TalkJSSession>
    );
  }

  return (
    <Routes>
        <Route
          path="/"
          element={<SignUpOnboarding isSocialLogin={isSocialLogin} />}
        />
        <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

export default AppRoutes;
